import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { AuthContext, type Profile, type SocialProvider } from './useAuth';
import { hasRecentRecoveryAmr, type AuthClaims } from '@/lib/authClaims';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAgreements, setHasAgreements] = useState(false);
  const [agreementsChecked, setAgreementsChecked] = useState(false);
  const [isRecoverySession, setIsRecoverySession] = useState(false);
  const isInitialSession = useRef(true);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProfile(data as Profile);
        return data as Profile;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  // trigger(on_auth_user_created)가 profile을 생성하므로, 구버전 사용자 등
  // profile이 누락된 예외 케이스에만 보강한다. trigger가 이미 생성한 경우
  // unique 충돌(23505)이 발생하지만 정상 상황이므로 무시한다.
  const ensureProfile = useCallback(async (userId: string, nickname: string) => {
    try {
      await supabase.from('profiles').insert({ id: userId, nickname, role: 'user' });
    } catch {
      // best-effort
    }
  }, []);

  const fetchAgreements = useCallback(async () => {
    // 활성 약관 전체 동의 여부는 서버 RPC(has_required_agreements)로 판단한다.
    // 동의 이력 한 건만으로 전체 동의로 간주하지 않는다(버전 일치까지 검증).
    // 일시적 오류 대비 1회 재시도. 실패 시에도 게이트가 우회되지 않도록(fail-closed)
    // hasAgreements를 false로 유지해 TermsGate가 동의 화면을 표시하게 한다.
    let succeeded = false;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const { data, error } = await supabase.rpc('has_required_agreements');
        if (error) throw error;
        setHasAgreements(data === true);
        succeeded = true;
        break;
      } catch {
        // 재시도
      }
    }
    if (!succeeded) {
      setHasAgreements(false);
    }
    setAgreementsChecked(true);
  }, []);

  const logActivity = useCallback(async (userId: string, action: string, detail: Record<string, unknown> = {}) => {
    try {
      await supabase.from('user_activity_logs').insert({
        user_id: userId,
        action,
        detail,
      });
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSession(currentSession);
      if (currentSession?.user) {
        setUser(currentSession.user);
        const p = await fetchProfile(currentSession.user.id);
        if (!p) {
          const fallbackName = currentSession.user.user_metadata?.full_name
            || currentSession.user.user_metadata?.name
            || currentSession.user.user_metadata?.nickname
            || '사용자';
          await ensureProfile(currentSession.user.id, fallbackName);
          await fetchProfile(currentSession.user.id);
        }
        await fetchAgreements();
        await logActivity(currentSession.user.id, 'login');
      }
      isInitialSession.current = false;
      setLoading(false);
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        setUser(newSession.user);
        fetchProfile(newSession.user.id).then((p) => {
          if (!p) {
            const fallbackName = newSession.user.user_metadata?.full_name
              || newSession.user.user_metadata?.name
              || newSession.user.user_metadata?.nickname
              || '사용자';
            ensureProfile(newSession.user.id, fallbackName).then(() => {
              fetchProfile(newSession.user.id);
            });
          }
        });
        fetchAgreements();
        // Log activity only for real sign-ins (not the initial session restore)
        if (event === 'SIGNED_IN' && !isInitialSession.current) {
          logActivity(newSession.user.id, 'login', {
            provider: newSession.user.app_metadata?.provider || 'email',
          });
        }
      } else {
        setUser(null);
        setProfile(null);
        setHasAgreements(false);
        setAgreementsChecked(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile, ensureProfile, fetchAgreements, logActivity]);

  // recovery 세션 판별: 세션 토큰을 getClaims()로 서명 검증한 뒤 amr recovery를 확인.
  // 검증 중에는 fail-closed(false). 세션이 바뀌거나 unmount되면 이전 비동기 응답을 무시.
  useEffect(() => {
    const token = session?.access_token ?? null;
    if (!token) {
      setIsRecoverySession(false);
      return;
    }

    let cancelled = false;
    setIsRecoverySession(false); // 검증 중 fail-closed

    supabase.auth
      .getClaims(token)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data?.claims) {
          setIsRecoverySession(false);
          return;
        }
        setIsRecoverySession(hasRecentRecoveryAmr(data.claims as AuthClaims));
      })
      .catch(() => {
        if (!cancelled) setIsRecoverySession(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };
      if (data.user) {
        await fetchProfile(data.user.id);
        await fetchAgreements();
        await logActivity(data.user.id, 'login');
      }
      return { error: null };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '로그인 중 오류가 발생했습니다.';
      return { error: message };
    }
  }, [fetchProfile, fetchAgreements, logActivity]);

  const signUp = useCallback(async (email: string, password: string, nickname: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { nickname: nickname || '사용자' } },
      });
      if (error) return { error: error.message, user: null, hasSession: false };

      const hasSession = !!data.session;

      if (data.user) {
        if (hasSession) {
          await ensureProfile(data.user.id, nickname || '사용자');
          await fetchProfile(data.user.id);
          await fetchAgreements();
        }
        await logActivity(data.user.id, 'register');
      }
      return { error: null, user: data.user, hasSession };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '회원가입 중 오류가 발생했습니다.';
      return { error: message, user: null, hasSession: false };
    }
  }, [fetchProfile, ensureProfile, fetchAgreements, logActivity]);

  const signOut = useCallback(async () => {
    if (user) {
      await logActivity(user.id, 'logout');
    }
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
    setHasAgreements(false);
    setAgreementsChecked(false);
    setIsRecoverySession(false);
  }, [user, logActivity]);

  const signInWithOAuth = useCallback(async (provider: SocialProvider) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });
      if (error) return { error: error.message };
      return { error: null };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '소셜 로그인 중 오류가 발생했습니다.';
      return { error: message };
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  const recordAgreements = useCallback(async (): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.rpc('record_agreements');
      if (error) return { error: error.message };
      if (user) {
        await fetchAgreements();
      }
      return { error: null };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '약관 동의 저장 중 오류가 발생했습니다.';
      return { error: message };
    }
  }, [user, fetchAgreements]);

  // fail-closed: 약관 조회가 실패해도(hasAgreements=false) 게이트를 우회하지 않는다.
  const needsTermsConsent =
    !!user && !loading && agreementsChecked && !hasAgreements;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        signInWithOAuth,
        refreshProfile,
        isAdmin: profile?.role === 'admin',
        hasAgreements,
        agreementsChecked,
        recordAgreements,
        needsTermsConsent,
        isRecoverySession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}